import React from "react"
import styled, { ThemeProvider } from "styled-components"

import { useComponentizedPage } from "store/contexts/componentized-page"
import componentThemeExtension from "utils/componentThemeExtension"
import { when, maxWidth, minWidth } from "utils"

import TileGrid from "components/tile-grid"
import ContentfulCard from "./card"
import ContentfulVideo from "./video"
import ContentfulButton from "./button"

const ContentfulTileGrid = ({ onTop, component, elRef }) => {
  const {
    id,
    heading,
    subheading,
    subheadingLogo,
    backgroundColor,
    items,
    mobileItemCount,
    navigationAnchor,
    hideOnPageLoad,
    headingFont,
    button,
    theme,
  } = component

  if (!items || !items.length) {
    return null
  }

  const jumboSlot = items.length === 6 ? items[0] : null
  const largeSlot = items.length === 6 ? items[1] : items[0]
  const regularSlots = items.filter((i, idx) => (items.length === 6 ? idx > 1 : idx > 0))

  const renderArticlesWithoutImage = items.length === 7 ? items.slice(-2) : null

  const title = heading && heading.childMarkdownRemark.html.replace(/^<p>(.*)<\/p>$/i, "$1")

  const styles = {}
  if (onTop) {
    styles.paddingTop = heading ? "2rem" : 0
  }

  const { isHomePage } = useComponentizedPage()
  const hideOnMobile = (i) => mobileItemCount && mobileItemCount > 0 && i >= mobileItemCount
  const useHorizontalScrollOnMobile = isHomePage && ["Article"].includes(items[0].itemType)
  const enforceCardWidth = useHorizontalScrollOnMobile
  const enforceCardColumnGap = useHorizontalScrollOnMobile

  const noImageCards = renderArticlesWithoutImage?.map((slot, idx) => (
    <ContentfulCard
      key={slot?.id}
      component={slot}
      tileVariant={items.length === 3 ? "tileMedium" : "tileSmall"}
      variant="4col"
      headlineVariant={items.length === 8 && idx < 2 ? "large" : "medium"}
      isGrid
      {...(isHomePage && {
        pageType: "Landing",
        zoomImageOnHover: true,
        squashImageVertically: true,
      })}
      renderImage={false}
    />
  ))

  return (
    <ThemeProvider theme={componentThemeExtension(backgroundColor, false, onTop, theme)}>
      <TileGrid
        hideOnPageLoad={hideOnPageLoad}
        dataId={id}
        id={navigationAnchor && navigationAnchor.anchorId}
        elRef={elRef}
        title={title}
        subtitle={subheading && subheading.childMarkdownRemark.html}
        logo={subheadingLogo?.length && subheadingLogo[0].secure_url}
        logoAspectRatio={
          subheadingLogo?.length && subheadingLogo[0].height / subheadingLogo[0].width
        }
        useMonoline={headingFont === "mbg Monoline"}
        jumboSlotCard={
          jumboSlot && (
            <ContentfulCard
              component={jumboSlot}
              tileVariant="tileJumbo"
              variant="2col"
              headlineVariant="xlarge"
              isGrid
              {...(isHomePage && {
                pageType: "Landing",
                zoomImageOnHover: true,
                squashImageVertically: true,
              })}
            />
          )
        }
        largeSlotCard={
          <ContentfulCard
            component={largeSlot}
            tileVariant={items.length === 3 ? "tileLarge" : "tileTall"}
            variant="2col"
            headlineVariant="xlarge"
            isGrid
            {...(isHomePage && {
              pageType: "Landing",
              zoomImageOnHover: true,
              squashImageVertically: true,
            })}
          />
        }
        renderWithoutImages={noImageCards}
        style={styles}
        {...(isHomePage && {
          useHeadingLine: true,
          largeOuterPadding: true,
          largeColMargin: true,
          leftWidth: items.length === 5 ? 51 : 50,
        })}
        buttonElement={button && <ContentfulButton component={button} />}
        useHorizontalScrollOnMobile={useHorizontalScrollOnMobile}
      >
        {regularSlots.map((slot, idx) =>
          slot.internal.type === "ContentfulVideo" ? (
            <ContentfulVideo key={slot.id} component={slot} isGrid nested />
          ) : (
            <MobileContainer
              key={slot.id}
              hideOnMobile={hideOnMobile(idx)}
              {...(enforceCardWidth && { width: "31.6rem" })}
              {...(enforceCardColumnGap && { columnGap: "1.8rem" })}
            >
              <ContentfulCard
                component={slot}
                tileVariant={items.length === 3 ? "tileMedium" : "tileSmall"}
                variant="4col"
                headlineVariant={items.length === 8 && idx < 2 ? "large" : "medium"}
                fullHeight
                isGrid
                {...(isHomePage && {
                  pageType: "Landing",
                  zoomImageOnHover: true,
                  squashImageVertically: true,
                })}
              />
            </MobileContainer>
          ),
        )}
      </TileGrid>
    </ThemeProvider>
  )
}

const MobileContainer = styled.div`
  ${when("hideOnMobile")`
    display: none;
  `}

  ${minWidth.mobile`
    display: block;
  `}

  ${maxWidth.mobile`
    ${when("width")`
      width: ${({ width }) => width};
    `}

    ${when("columnGap")`
      margin: 0 ${({ columnGap }) => columnGap};

      &:first-of-type {
        margin-left: 0;
      }

      &:last-of-type {
        margin-right: 0;
      }
    `}
  `}
`

export default ContentfulTileGrid
